import { useEffect, useState, useCallback, Fragment } from 'react';
import { useParams } from "react-router-dom";
import {
    Button,
    Row,
    Col,
    Collapse,
    Card,
    CardBody,
    Table,
    Badge,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import '../moveinout.scss';
import { conditionLabels, sectionDisplayNames, sectionIcons } from '../Generate/Components/FormikHelpers/utils';
import { SERVER_URL } from '../../../ServerLink';

const MEDIA_PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60"%3E%3Crect width="60" height="60" fill="%23edf2f7"/%3E%3Cpath d="M18 38l8-9 6 6 5-5 7 8H18z" fill="%2394a3b8"/%3E%3Ccircle cx="24" cy="22" r="4" fill="%2394a3b8"/%3E%3C/svg%3E';

const MediaViewerModal = ({ isOpen, toggle, mediaUrl, mediaType, title }) => (
    <div className={`modal ${isOpen ? 'd-block' : 'd-none'}`} style={{ backgroundColor: 'rgba(0,0,0,0.9)' }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content bg-dark text-white border-0">
                <div className="modal-header border-0 pb-2">
                    <h5 className="modal-title">{title || 'Media Preview'}</h5>
                    <button type="button" className="btn-close btn-close-white" onClick={toggle}></button>
                </div>
                <div className="modal-body text-center py-5">
                    {mediaType?.startsWith('video/') ? (
                        <video
                            controls
                            style={{
                                maxHeight: '85vh',
                                maxWidth: '100%',
                                borderRadius: '12px',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
                            }}
                        >
                            <source src={mediaUrl} type={mediaType} />
                            Your browser does not support the video tag.
                        </video>
                    ) : (
                        <img
                            src={mediaUrl}
                            alt={title}
                            style={{
                                maxHeight: '85vh',
                                maxWidth: '100%',
                                objectFit: 'contain',
                                borderRadius: '12px',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
                            }}
                        />
                    )}
                </div>
                <div className="modal-footer border-0 justify-content-center">
                    <Button color="secondary" onClick={toggle}>
                        Close
                    </Button>
                </div>
            </div>
        </div>
    </div>
);

const DisplayDate = ({ dateStr }) => {
    if (!dateStr) return <span>____________________</span>;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return <span>____________________</span>;

    const day = date.getDate();
    const year = date.getFullYear();
    const monthName = date.toLocaleString('default', { month: 'long' });

    const suffix =
        [1, 21, 31].includes(day) ? 'st' :
            [2, 22].includes(day) ? 'nd' :
                [3, 23].includes(day) ? 'rd' : 'th';

    return (
        <span style={{ fontWeight: '600' }}>
            {`${monthName} ${day}${suffix}, ${year}`}
        </span>
    );
};

const DisplayText = ({ value, placeholder = '____________________' }) => (
    <span style={{ fontWeight: '600' }}>{value || placeholder}</span>
);

const DisplaySignature = ({ signatureData }) => {
    if (!signatureData) {
        return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>Not signed</span>;
    }
    return <img src={signatureData} alt="Signature" style={{ maxHeight: '18px', verticalAlign: 'middle' }} />;
};

const InspectionItemRowPreview = ({
    name,
    move_in_condition,
    move_out_condition,
    signedUrlCache,
    getMediaDisplayUrl,
    setSelectedMedia,
}) => {
    const attachments_in = move_in_condition?.attachments || [];
    const attachments_out = move_out_condition?.attachments || [];

    const handleMediaClick = async (attachment, phaseTitle) => {
        const url = await getMediaDisplayUrl(attachment);
        if (url && !url.includes('placeholder')) {
            const mediaType = attachment.endsWith('.mp4') || attachment.endsWith('.mov') || attachment.endsWith('.webm')
                ? 'video/mp4'
                : 'image/jpeg';
            setSelectedMedia({ url, title: `${name} - ${phaseTitle}`, type: mediaType });
        }
    };

    const renderThumbnail = useCallback((file, cacheKey, phase, idx) => {
        const isVideo = typeof file === 'string' && (file.endsWith('.mp4') || file.endsWith('.mov') || file.endsWith('.webm'));
        let src = signedUrlCache[cacheKey] || signedUrlCache[file];

        if (!src) {
            src = MEDIA_PLACEHOLDER;
        }

        const borderColor = phase === 'move_in' ? '#3b82f6' : '#ef4444';

        return (
            <div key={`${phase}-${idx}`} className="position-relative">
                {isVideo ? (
                    <div
                        className="rounded shadow-sm d-flex align-items-center justify-content-center"
                        style={{
                            width: 60,
                            height: 60,
                            backgroundColor: '#000',
                            cursor: 'pointer',
                            border: `2px solid ${borderColor}`,
                        }}
                        onClick={() => handleMediaClick(file, phase === 'move_in' ? 'Move-In' : 'Move-Out')}
                    >
                        <FeatherIcon icon="play-circle" size={32} className="text-white" />
                    </div>
                ) : (
                    <img
                        src={src}
                        alt={phase}
                        className="rounded shadow-sm"
                        style={{
                            width: 60,
                            height: 60,
                            objectFit: 'cover',
                            cursor: 'pointer',
                            border: `2px solid ${borderColor}`,
                        }}
                        onClick={() => handleMediaClick(file, phase === 'move_in' ? 'Move-In' : 'Move-Out')}
                    />
                )}
            </div>
        );
    }, [signedUrlCache, handleMediaClick]);

    return (
        <tr className="align-middle">
            <td className="fw-semibold px-4 py-3" style={{ fontSize: '0.9rem' }}>
                <div className="d-flex align-items-center">
                    <FeatherIcon icon="check-square" size={16} className="me-2 text-muted" />
                    {name}
                </div>
            </td>
            <td className="text-center px-3 py-3">
                <div className="py-1 fw-medium">
                    {move_in_condition?.code != null ? conditionLabels[move_in_condition.code] : <em className="text-muted">Not Set</em>}
                </div>
            </td>
            <td className="px-3 py-3">
                <p className="mb-0 text-muted">{move_in_condition?.comments || 'No comments'}</p>
            </td>
            <td className="text-center px-2 py-3">
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                    {attachments_in.map((att, i) => {
                        const key = `in-${att}-${i}`;
                        return renderThumbnail(att, key, 'move_in', i);
                    })}
                    {attachments_in.length === 0 && <small className="text-muted">No media</small>}
                </div>
            </td>
            <td className="text-center px-3 py-3">
                <div className="py-1 fw-medium">
                    {move_out_condition?.code != null ? conditionLabels[move_out_condition.code] : <em className="text-muted">Not Set</em>}
                </div>
            </td>
            <td className="px-3 py-3">
                <p className="mb-0 text-muted">{move_out_condition?.comments || 'No comments'}</p>
            </td>
            <td className="text-center px-2 py-3">
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                    {attachments_out.map((att, i) => {
                        const key = `out-${att}-${i}`;
                        return renderThumbnail(att, key, 'move_out', i);
                    })}
                    {attachments_out.length === 0 && <small className="text-muted">No media</small>}
                </div>
            </td>
        </tr>
    );
};


const ConclusionsSection = ({ moveInConclusions, moveOutConclusions }) => (
    <Card className="shadow-sm border-0 mb-5">
        <CardBody className="p-5">
            <div className="text-primary mb-4 d-flex align-items-center fw-semibold">
                <FeatherIcon icon="edit-3" size={20} strokeWidth={2.5} className="me-2" />
                Conclusions & Recommendations
            </div>

            {/* Move-In Conclusions */}
            <div className="mb-5 p-4 bg-white border border-primary rounded">
                <h6 className="fw-bold mb-3 text-primary" style={{ fontSize: '1.1rem' }}>
                    Move-In Conclusions / Recommendations
                </h6>
                <div
                    className="p-3 bg-light rounded border"
                    style={{ minHeight: '80px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                    {moveInConclusions?.trim() ? (
                        moveInConclusions
                    ) : (
                        <em className="text-muted">No conclusions or recommendations recorded for move-in.</em>
                    )}
                </div>
            </div>

            {/* Move-Out Conclusions */}
            <div className="p-4 bg-white border border-danger rounded">
                <h6 className="fw-bold mb-3 text-danger" style={{ fontSize: '1.1rem' }}>
                    Move-Out Conclusions / Recommendations
                </h6>
                <div
                    className="p-3 bg-light rounded border"
                    style={{ minHeight: '80px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                    {moveOutConclusions?.trim() ? (
                        moveOutConclusions
                    ) : (
                        <em className="text-muted">No conclusions or recommendations recorded for move-out.</em>
                    )}
                </div>
            </div>
        </CardBody>
    </Card>
);

const AllMediaGallery = ({ allMedia, setSelectedMedia }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    if (allMedia.length === 0) return null;
    const visibleIndex = Math.min(activeIndex, allMedia.length - 1);
    const activeMedia = allMedia[visibleIndex];
    const selectMedia = (index) => setActiveIndex((index + allMedia.length) % allMedia.length);
    const showPrevious = () => selectMedia(visibleIndex - 1);
    const showNext = () => selectMedia(visibleIndex + 1);

    return (
        <Card className="shadow-sm border-0 mb-5">
            <CardBody className="p-5">
                <div className="text-primary mb-4 d-flex align-items-center justify-content-between fw-semibold">
                    <span className="d-flex align-items-center">
                    <FeatherIcon icon="image" size={20} strokeWidth={2.5} className="me-2" />
                        All Media Gallery
                    </span>
                    <span className="text-muted small fw-normal">{visibleIndex + 1} of {allMedia.length}</span>
                </div>

                <div className="position-relative rounded overflow-hidden bg-dark" style={{ minHeight: '320px' }}>
                    {activeMedia.type.startsWith('video') ? (
                        <video controls style={{ width: '100%', height: '420px', objectFit: 'contain', display: 'block' }}>
                            <source src={activeMedia.url} type={activeMedia.type} />
                        </video>
                    ) : (
                        <img src={activeMedia.url} alt={activeMedia.title} style={{ width: '100%', height: '420px', objectFit: 'contain', display: 'block' }} />
                    )}
                    {allMedia.length > 1 && <>
                        <Button type="button" color="light" className="position-absolute top-50 start-0 translate-middle-y ms-3 rounded-circle p-2" onClick={showPrevious} aria-label="Previous image"><FeatherIcon icon="chevron-left" size={22} /></Button>
                        <Button type="button" color="light" className="position-absolute top-50 end-0 translate-middle-y me-3 rounded-circle p-2" onClick={showNext} aria-label="Next image"><FeatherIcon icon="chevron-right" size={22} /></Button>
                    </>}
                    <Button type="button" color="dark" className="position-absolute bottom-0 end-0 m-3" onClick={() => setSelectedMedia(activeMedia)}>
                        <FeatherIcon icon="maximize-2" size={15} className="me-1" /> Open
                    </Button>
                </div>

                <p className="text-center text-muted small mt-3 mb-3">
                    {activeMedia.itemName} <Badge color={activeMedia.phase === 'move_in' ? 'primary' : 'danger'} pill className="ms-1">{activeMedia.phase === 'move_in' ? 'Move-In' : 'Move-Out'}</Badge>
                </p>

                <div className="d-flex gap-2 overflow-auto pb-1" aria-label="Gallery thumbnails">
                    {allMedia.map((media, idx) => (
                        <button key={`${media.url}-${idx}`} type="button" className={`border rounded p-0 flex-shrink-0 ${idx === visibleIndex ? 'border-primary border-2' : 'border-light'}`} style={{ width: 72, height: 72, overflow: 'hidden', background: '#fff' }} onClick={() => selectMedia(idx)} aria-label={`Show ${media.title}`}>
                            {media.type.startsWith('video') ? <div className="h-100 bg-dark text-white d-flex align-items-center justify-content-center"><FeatherIcon icon="play" size={24} /></div> : <img src={media.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                        </button>
                    ))}
                </div>
            </CardBody>
        </Card>
    );
};

const MoveInMoveOutInspectionUI = ({ inspectionData, token: tokenProp }) => {
    const params = useParams();
    const token = tokenProp || params?.token;
    const [selectedMedia, setSelectedMedia] = useState({ url: '', title: '', type: '' });
    const [openSections, setOpenSections] = useState([]);
    const [signedUrlCache, setSignedUrlCache] = useState({});

    const toggleSection = (key) => {
        setOpenSections(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
    };

    const getMediaKey = useCallback((attachment) => {
        if (typeof attachment !== 'string') return null;
        try {
            const url = new URL(attachment);
            return url.pathname.replace(/^\/+/, '');
        } catch {
            return attachment;
        }
    }, []);

    const getMultipleSignedUrls = async (keys) => {
        if (!Array.isArray(keys) || keys.length === 0) return {};

        try {
            const response = await fetch(
                `${SERVER_URL}/v2/move-in-out-inspections/public/signed-urls`,
                {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ keys }),
                }
            );

            if (!response.ok) {
                throw new Error("Failed to generate signed URLs");
            }

            const result = await response.json();
            return result.data || {};
        } catch (error) {
            console.error("Failed to generate signed URLs", error);
            return {};
        }
    };

    const getMediaDisplayUrl = useCallback(
        async (attachment) => {
            const key = getMediaKey(attachment);
            if (!key) return null;

            if (signedUrlCache[key]) return signedUrlCache[key];

            try {
                const urlMap = await getMultipleSignedUrls([key]);
                const signed = urlMap[key];

                if (signed) {
                    setSignedUrlCache((prev) => ({ ...prev, [key]: signed }));
                    return signed;
                }
            } catch (err) {
                console.error("Signed URL fetch failed:", err);
            }

            return null;
        },
        [signedUrlCache]
    );

    useEffect(() => {
        const keys = [];
        inspectionData?.inspection_items?.forEach(item => {
            (item.move_in_condition?.attachments || []).forEach(a => {
                const k = getMediaKey(a);
                if (k) keys.push(k);
            });
            (item.move_out_condition?.attachments || []).forEach(a => {
                const k = getMediaKey(a);
                if (k) keys.push(k);
            });
        });
        if (keys.length > 0) {
            getMultipleSignedUrls([...new Set(keys)]).then(urlMap => {
                setSignedUrlCache(urlMap);
            }).catch(console.error);
        }
    }, [inspectionData, getMediaKey]);

    const groupItemsBySection = () => {
        const groups = {};
        (inspectionData?.inspection_items || []).forEach((item, idx) => {
            const sec = item.section || 'other_duplex';
            if (!groups[sec]) groups[sec] = [];
            groups[sec].push({ ...item, index: idx });
        });
        return groups;
    };

    const tenants = inspectionData?.property_details?.tenants || [];
    const tenantNames = tenants.map(t => t.name?.trim()).filter(Boolean);
    const tenantList = tenantNames.length > 0
        ? tenantNames.length === 1
            ? tenantNames[0]
            : `${tenantNames.slice(0, -1).join(', ')} and ${tenantNames[tenantNames.length - 1]}`
        : '____________________';

    const allMedia = (inspectionData?.inspection_items || []).flatMap((item) => (
        ['move_in', 'move_out'].flatMap((phase) => (
            (item[`${phase}_condition`]?.attachments || []).map((attachment) => {
                const url = signedUrlCache[getMediaKey(attachment)];
                if (!url) return null;
                const isVideo = /\.(mp4|mov|avi|wmv|webm)$/i.test(attachment);
                return {
                    url,
                    title: `${item.name} - ${phase === 'move_in' ? 'Move-In' : 'Move-Out'}`,
                    type: isVideo ? 'video/mp4' : 'image/jpeg',
                    phase,
                    itemName: item.name,
                };
            })
        ))
    )).filter(Boolean);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ backgroundColor: '#0D568F', minHeight: '64px' }}>
                <h5 className="mb-0 text-white fw-normal d-flex align-items-center">
                    <FeatherIcon icon="eye" size={22} className="me-2" />
                    Move-In / Move-Out Inspection Report (Tenant Preview)
                </h5>
                <img src="/images/rentdigicare-logo.svg" alt="logo" style={{ height: '34px' }} />
            </div>

            <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa' }}>
                {/* Property & Parties */}
                <Card className="shadow-sm border-0 mb-4">
                    <CardBody>
                        <div className="text-primary mb-3 d-flex align-items-center fw-semibold">
                            <FeatherIcon icon="map-pin" size={17} strokeWidth={2.5} className="mb-1 me-2" />
                            Property & Parties Information
                        </div>
                        <Row className="g-4">
                            <Col md={5}>
                                <strong>Rental Premises Address:</strong><br />
                                {inspectionData?.property_details?.address || 'Not provided'}
                            </Col>
                            <Col md={3}>
                                <strong>Unit / Suite:</strong><br />
                                {inspectionData?.property_details?.unit_number || '—'}
                            </Col>
                            <Col md={4}>
                                <strong>Landlord:</strong><br />
                                {inspectionData?.property_details?.landlord?.name || 'Not provided'}
                            </Col>
                            <Col md={12}>
                                <strong>Tenants:</strong><br />
                                {tenants.length > 0 ? tenants.map((t, i) => (
                                    <div key={i}>• {t.name} ({t.email})</div>
                                )) : 'No tenants listed'}
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Inspection Items per Section */}
                {Object.entries(groupItemsBySection()).map(([sectionKey, groupItems]) => {
                    const title = sectionDisplayNames[sectionKey] || sectionKey.replace(/_/g, ' ').toUpperCase();
                    const icon = sectionIcons[sectionKey] || 'home';
                    const isOpen = openSections.includes(sectionKey);

                    const totalMedia = groupItems.reduce((sum, item) => {
                        return sum + (item.move_in_condition?.attachments?.length || 0) + (item.move_out_condition?.attachments?.length || 0);
                    }, 0);

                    return (
                        <Card key={sectionKey} className="mb-4 shadow-sm border-0 overflow-hidden">
                            <div
                                className="d-flex align-items-center justify-content-between p-4 text-primary bg-light"
                                onClick={() => toggleSection(sectionKey)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex align-items-center fw-semibold" style={{ fontSize: '0.95rem' }}>
                                    <FeatherIcon icon={icon} size={18} strokeWidth={2.5} className="me-2" />
                                    {title}
                                </div>
                                <div className="d-flex align-items-center gap-4">
                                    {groupItems.length} items
                                    {totalMedia > 0 && (
                                        <div className="d-flex align-items-center gap-2 ps-2 border-start border-secondary-subtle">
                                            <FeatherIcon icon="image" size={16} className="text-muted" />
                                            <span className="text-muted">{totalMedia} media</span>
                                        </div>
                                    )}
                                    <FeatherIcon icon={isOpen ? 'chevron-up' : 'chevron-down'} size={24} className="text-primary ms-auto" />
                                </div>
                            </div>

                            <Collapse isOpen={isOpen}>
                                <CardBody className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="moveinout-table align-middle mb-0">
                                            <thead className="bg-light border-bottom border-2">
                                                <tr>
                                                    <th className="text-muted fw-semibold text-uppercase text-start th-item">Item</th>
                                                    <th className="text-muted fw-semibold text-uppercase text-center">Move-In Condition</th>
                                                    <th className="text-muted fw-semibold text-uppercase">Move-In Notes</th>
                                                    <th className="text-muted fw-semibold text-uppercase text-center">Move-In Media</th>
                                                    <th className="text-muted fw-semibold text-uppercase text-center">Move-Out Condition</th>
                                                    <th className="text-muted fw-semibold text-uppercase">Move-Out Notes</th>
                                                    <th className="text-muted fw-semibold text-uppercase text-center">Move-Out Media</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupItems.map(item => (
                                                    <Fragment key={item.index}>
                                                        <InspectionItemRowPreview
                                                            name={item.name}
                                                            move_in_condition={item.move_in_condition}
                                                            move_out_condition={item.move_out_condition}
                                                            signedUrlCache={signedUrlCache}
                                                            getMediaDisplayUrl={getMediaDisplayUrl}
                                                            setSelectedMedia={setSelectedMedia}
                                                        />
                                                        <tr><td colSpan={7} className="p-0 border-bottom" style={{ opacity: 0.25 }} /></tr>
                                                    </Fragment>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Collapse>
                        </Card>
                    );
                })}

                {/* Keys */}
                <Card className="shadow-sm border-0 mb-4">
                    <CardBody className="p-4">
                        <div className="text-primary mb-4 d-flex align-items-center fw-semibold">
                            <FeatherIcon icon="lock" size={17} strokeWidth={2.5} className="mb-1 me-2" />
                            Keys Issued / Returned
                        </div>
                        <Row className="g-3">
                            {['suite', 'building', 'entry', 'garage', 'mailbox', 'fob', 'parking_pass', 'discount_card'].map(keyType => (
                                <Col key={keyType}>
                                    <div className="border rounded p-3 text-center">
                                        <div className="text-capitalize fw-bold text-secondary small mb-2">{keyType} Key</div>
                                        <div><>Issued:</> {inspectionData?.keys?.issued?.[keyType] ?? 0}</div>
                                        <div><>Returned:</> {inspectionData?.keys?.returned?.[keyType] ?? 0}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </CardBody>
                </Card>

                {/* Signatures & Dates */}
                <Card className="shadow-sm border-0 mb-5">
                    <CardBody className="p-5">
                        <div className="mb-5 p-4 bg-white border border-dark rounded">
                            <h6 className="fw-bold text-center mb-3" style={{ fontSize: '1.1rem' }}>IN CONDITION</h6>
                            <p className="mb-2" style={{ fontSize: '1rem' }}>
                                Inspections should be conducted when the premises are vacant unless the landlord and tenant or their agents otherwise agree.
                            </p>

                            <h6 className="fw-bold mb-3">WHEN THE LANDLORD AND TENANT INSPECT THE PREMISES TOGETHER: (Complete #1 and either #2a, b or c)</h6>
                            <span className="mb-1">
                                1) The inspection of the premises was done on <DisplayDate dateStr={inspectionData?.move_in?.['1']?.inspection_date} /> by <DisplayText value={inspectionData?.move_in?.['1']?.landlord_name} /> and by {tenantList}.
                            </span>
                            <div className="mb-1">
                                <div className="d-flex align-items-end gap-2">
                                    <span className="text-dark font-semibold">Signature of Landlord or Landlord's Agent:</span>
                                    <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                                        <DisplaySignature signatureData={inspectionData.move_in['1'].landlord_signature} />
                                    </div>
                                    <span className="text-dark font-semibold ms-3">Date:</span>
                                    <DisplayDate dateStr={inspectionData?.move_in?.['1']?.landlord_signature_date} />
                                </div>
                            </div>

                            <div>
                                <p className="mb-2">
                                    <div className="d-flex align-items-end gap-2">
                                        <span className="text-dark font-semibold">
                                            2 a)  <DisplayText value={inspectionData?.move_in?.['2']['a']?.tenant_name} /> , agree that this report fairly represents the condition of the premises.{' '}
                                        </span>
                                        <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                                            <DisplaySignature signatureData={inspectionData.move_in['2'].a.tenant_signature} />
                                        </div>
                                    </div>
                                </p>

                                <p className="mb-2">
                                    OR b) <DisplayText value={inspectionData?.move_in?.['2'].b?.tenant_name} /> , disagree that this report fairly represents the condition of the premises for the following reasons: <DisplayText value={inspectionData?.move_in?.['2'].b?.disagreement_reasons} />
                                </p>

                                <p className="mb-2">
                                    <div className="d-flex align-items-end gap-2">
                                        <span className="text-dark font-semibold">
                                            OR c) The Tenant or Tenant's agent present at the inspection refused to sign the Tenant's Statement.{' '}
                                        </span>
                                        <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                                            <DisplaySignature signatureData={inspectionData.move_in['2'].c.landlord_refusal_signature} />
                                        </div>
                                    </div>
                                </p>
                            </div>
                            <h6 className="fw-bold mt-4 mb-3">
                                WHEN THE LANDLORD INSPECTS WITHOUT THE TENANT:
                            </h6>

                            <p className="mb-1">
                                3) The inspection of the premises was done on{' '}
                                <DisplayDate dateStr={inspectionData?.move_in?.['3']?.inspection_date} />{' '}
                                by{' '}
                                <DisplayText value={inspectionData?.move_in?.['3']?.landlord_name} />{' '}
                                without the Tenant or Tenant's Agent being present.
                            </p>

                            <div className="mb-4">
                                <div className="d-flex align-items-end gap-2 flex-wrap">
                                    <span className="text-dark font-semibold">
                                        Signature of Landlord or Landlord's Agent:
                                    </span>

                                    <div
                                        className="border-bottom border-dark flex-grow-1"
                                        style={{ minWidth: '200px', maxWidth: '300px' }}
                                    >
                                        <DisplaySignature
                                            signatureData={inspectionData?.move_in?.['3']?.landlord_signature}
                                        />
                                    </div>

                                    <span className="text-dark font-semibold ms-3">Date:</span>
                                    <DisplayDate
                                        dateStr={inspectionData?.move_in?.['3']?.landlord_signature_date}
                                    />

                                    <span className="text-dark font-semibold ms-3">
                                        TENANT FORWARDING ADDRESS:
                                    </span>
                                    <DisplayText
                                        value={inspectionData?.move_in?.['3']?.forwarding_address}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Move-Out */}
                        <div className="mb-5 p-4 bg-white border border-dark rounded">
                            <h6 className="fw-bold text-center mb-3" style={{ fontSize: '1.1rem' }}>OUT CONDITION</h6>
                            <p className="mb-2" style={{ fontSize: '1rem' }}>
                                Inspections should be conducted when the premises are vacant unless the landlord and tenant or their agents otherwise agree.
                            </p>

                            <h6 className="fw-bold mb-3">WHEN THE LANDLORD AND TENANT INSPECT THE PREMISES TOGETHER: (Complete #1 and either #2a, b or c)</h6>
                            <span className="mb-1">
                                1) The inspection of the premises was done on <DisplayDate dateStr={inspectionData?.move_out?.['1']?.inspection_date} /> by <DisplayText value={inspectionData?.move_out?.['1']?.landlord_name} /> and by {tenantList}.
                            </span>
                            <div className="mb-1">
                                <div className="d-flex align-items-end gap-2">
                                    <span className="text-dark font-semibold">Signature of Landlord or Landlord's Agent:</span>
                                    <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                                        <DisplaySignature signatureData={inspectionData.move_out['1'].landlord_signature} />
                                    </div>
                                    <span className="text-dark font-semibold ms-3">Date:</span>
                                    <DisplayDate dateStr={inspectionData?.move_out?.['1']?.landlord_signature_date} />
                                </div>
                            </div>

                            <div>
                                <p className="mb-2">
                                    <div className="d-flex align-items-end gap-2">
                                        <span className="text-dark font-semibold">
                                            2 a)  <DisplayText value={inspectionData?.move_out?.['2']['a']?.tenant_name} /> , agree that this report fairly represents the condition of the premises.{' '}
                                        </span>
                                        <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                                            <DisplaySignature signatureData={inspectionData.move_out['2'].a.tenant_signature} />
                                        </div>
                                    </div>
                                </p>

                                <p className="mb-2">
                                    OR b) <DisplayText value={inspectionData?.move_out?.['2'].b?.tenant_name} /> , agree that this report fairly represents the condition of the premises, disagree that this report fairly represents the condition of the premises for the following reasons: <DisplayText value={inspectionData?.move_in?.['2'].b?.disagreement_reasons} />
                                </p>

                                <p className="mb-2">
                                    <div className="d-flex align-items-end gap-2">
                                        <span className="text-dark font-semibold">
                                            OR c) The Tenant or Tenant's agent present at the inspection refused to sign the Tenant's Statement.{' '}
                                        </span>
                                        <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                                            <DisplaySignature signatureData={inspectionData.move_out['2'].c.landlord_refusal_signature} />
                                        </div>
                                    </div>
                                </p>
                            </div>
                            <h6 className="fw-bold mt-4 mb-3">
                                WHEN THE LANDLORD INSPECTS WITHOUT THE TENANT:
                            </h6>

                            <p className="mb-1">
                                3) The inspection of the premises was done on{' '}
                                <DisplayDate dateStr={inspectionData?.move_out?.['3']?.inspection_date} />{' '}
                                by{' '}
                                <DisplayText value={inspectionData?.move_out?.['3']?.landlord_name} />{' '}
                                without the Tenant or Tenant's Agent being present.
                            </p>

                            <div className="mb-4">
                                <div className="d-flex align-items-end gap-2 flex-wrap">
                                    <span className="text-dark font-semibold">
                                        Signature of Landlord or Landlord's Agent:
                                    </span>

                                    <div
                                        className="border-bottom border-dark flex-grow-1"
                                        style={{ minWidth: '200px', maxWidth: '300px' }}
                                    >
                                        <DisplaySignature
                                            signatureData={inspectionData?.move_out?.['3']?.landlord_signature}
                                        />
                                    </div>

                                    <span className="text-dark font-semibold ms-3">Date:</span>
                                    <DisplayDate
                                        dateStr={inspectionData?.move_out?.['3']?.landlord_signature_date}
                                    />

                                    <span className="text-dark font-semibold ms-3">
                                        TENANT FORWARDING ADDRESS:
                                    </span>
                                    <DisplayText
                                        value={inspectionData?.move_out?.['3']?.forwarding_address}
                                    />
                                </div>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                {/* Conclusions */}
                <ConclusionsSection
                    moveInConclusions={inspectionData?.move_in_conclusions_recommendations}
                    moveOutConclusions={inspectionData?.move_out_conclusions_recommendations}
                />
                <AllMediaGallery
                    allMedia={allMedia}
                    setSelectedMedia={setSelectedMedia}
                />
            </div>

            {selectedMedia.url && (
                <MediaViewerModal
                    isOpen={!!selectedMedia.url}
                    toggle={() => setSelectedMedia({ url: '', title: '', type: '' })}
                    mediaUrl={selectedMedia.url}
                    mediaType={selectedMedia.type}
                    title={selectedMedia.title}
                />
            )}
        </>
    );
};

// `token` prop: rendered inside a dialog (mastersheet Edit Tenant); otherwise the emailed link's URL param.
const PreviewMoveInMoveOutInspection = ({ token: tokenProp }) => {
    const params = useParams();
    const token = tokenProp || params?.token;
    const [inspectionData, setInspectionData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchInspection = async () => {
            try {
                const response = await fetch(
                    `${SERVER_URL}/v2/move-in-out-inspections/public`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (!response.ok) {
                    throw new Error("Invalid or expired token");
                }

                const result = await response.json();
                setInspectionData(result.data);
            } catch (error) {
                console.error("Failed to fetch inspection preview", error);
            } finally {
                setLoading(false);
            }
        };

        fetchInspection();
    }, [token]);

    if (loading) return <div>Loading inspection preview...</div>;
    if (!inspectionData) return <div>Invalid or expired preview link</div>;

    return <MoveInMoveOutInspectionUI inspectionData={inspectionData} token={token} />;
};

export { MoveInMoveOutInspectionUI, PreviewMoveInMoveOutInspection };
